<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <Banner
    v-if="activeUser.emailStatus === 'unconfirmed' && isVisible"
    dismissStorageKey="hideEmailConfirmationBanner"
  >
    <p>
      <span class="material-icons-outlined">info</span>
      <span v-if="!hasError">{{
        activeUserEmailConfirmation
          ? $t('common:emailConfirmation.headerSent', {
              email: activeUserEmailConfirmation.email,
            })
          : $t('common:emailConfirmation.header')
      }}</span>
      <span v-if="hasError" class="error-message">
        {{ $t('common:emailConfirmation.sendError') }}
      </span>
    </p>
    <template #actions>
      <CButton
        v-if="!hasError"
        class="whitespace-nowrap"
        icon="email"
        :disabled="isLoading"
        @click="sendVerificationEmail"
      >
        {{
          activeUserEmailConfirmation
            ? $t('common:emailConfirmation.resendButton')
            : $t('common:emailConfirmation.confirmationButton')
        }}
      </CButton>
    </template>
  </Banner>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useApi, useToast, useTranslation } from '@/composables/plugins';
import { useActiveUserStore } from '@/stores/user';
import { useUser } from '@/composables/users';
import Banner from './Banner.vue';

const api = useApi();
const toast = useToast();
const { t } = useTranslation();
const isVisible = ref(true);
const hasError = ref(false);
const isLoading = ref(false);

const { activeUser } = useActiveUserStore();
const { user, fetchUser } = useUser();

onMounted(async () => {
  if (activeUser && activeUser.emailStatus === 'unconfirmed') {
    // refresh user
    await fetchUser(activeUser.id ?? '');
  }
});

const activeUserEmailConfirmation = computed(() =>
  user.value?.emailConfirmations?.find(
    (emailConfirmation) => emailConfirmation.resourceField === 'email'
  )
);

const sendVerificationEmail = async () => {
  hasError.value = false;
  isLoading.value = true;

  try {
    await api.post(
      activeUserEmailConfirmation.value
        ? `/v2/email-confirmations/${activeUserEmailConfirmation.value.id}/resend`
        : `/v2/users/${activeUser.id ?? ''}/request-email-confirmation`
    );
    isVisible.value = false;
    toast.success(t('common:emailConfirmation.confirmationSent'));
    await fetchUser(activeUser.id ?? '');
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.nowrap {
  white-space: nowrap;
}

p {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.material-icons-outlined {
  font-size: 1.25rem;
}

.error-message {
  color: var(--error);
}
</style>
